import { useState, useCallback } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { useAuth } from './useAuth'

export function useBiometricGate() {
  // FA-01-08: read sessionRef.current rather than the React `session`
  // state so the Authorization header carries the LATEST access_token.
  // Supabase rotates the JWT in the background (e.g. on tab focus), and
  // useAuth intentionally does not setSession on TOKEN_REFRESHED to
  // avoid cascading re-renders. Without the ref, biometric-challenge
  // and biometric-verify would 401 against an expired token.
  const { sessionRef } = useAuth()
  const [verifying, setVerifying] = useState(false)

  const verify = useCallback(async () => {
    setVerifying(true)
    try {
      const accessToken = sessionRef?.current?.access_token
      if (!accessToken) {
        return { verified: false, error: 'Session expired. Sign in again.' }
      }

      const optRes = await fetch('/api/auth/biometric-challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      })
      const options = await optRes.json()
      if (options.error) return { verified: false, error: options.error }

      // FA-01-passkey-hardening (D2): no silent pass-through on
      // real-money / sensitive actions. Login MFA and order MFA must be
      // aligned. The lockout-recovery path is password sign-in →
      // Settings → register passkey (self-serve in the current UI).
      if (!options.hasPasskeys) {
        return { verified: false, error: 'Passkey required. Set one up in Settings before sending orders.' }
      }

      // Trigger biometric prompt
      const { challengeId, hasPasskeys, ...authOptions } = options
      const assertion = await startAuthentication({ optionsJSON: authOptions })

      // Verify server-side — re-read the ref in case Supabase rotated
      // the JWT during the native prompt (typically 1-30s of user
      // interaction).
      const verifyToken = sessionRef?.current?.access_token
      if (!verifyToken) {
        return { verified: false, error: 'Session expired. Sign in again.' }
      }
      const verRes = await fetch('/api/auth/biometric-verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${verifyToken}`,
        },
        body: JSON.stringify({ response: assertion, challengeId }),
      })
      const result = await verRes.json()
      if (!result.verified) return { verified: false, error: result.error || 'Verification failed' }

      return { verified: true }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        return { verified: false, error: 'Biometric verification cancelled' }
      }
      return { verified: false, error: err.message }
    } finally {
      setVerifying(false)
    }
  }, [sessionRef])

  return { verify, verifying }
}
